// =============================================
// AuthorPagePreviewEditorModal — modal Podglad/HTML/Info dla strony autora (C1)
//
// 3 zakladki: Podglad (rendered HTML w prose) / HTML (textarea edytowalny) / Info (debug + schema).
// Toolbar: Edytuj / Przywroc wygenerowane / Kopiuj / Wygeneruj ponownie / Zapisz / Publikuj WP (placeholder).
// Zapis: UPDATE thematic_writing_profiles SET author_page_html, author_page_generated_at=now() WHERE id.
// Re-generate: woła generateAuthorPage(profileId, mapId) → podmienia HTML w modalu (NIE auto-save).
// =============================================

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Eye,
  Code,
  Info,
  Save,
  RefreshCw,
  Edit2,
  Copy,
  Check,
  RotateCcw,
  Loader2,
  Users,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  generateAuthorPage,
  type AuthorPageDebug,
} from "@/services/contentEngine/authorPageGenerator";
import { publishHtmlAsWpPage } from "@/services/uniquePages/publishHtmlAsWpPage";
import type { SchemaPayload } from "@/services/schema/types";

export interface AuthorPagePreviewEditorModalProps {
  open: boolean;
  profileId: string;
  profileLabel: string;
  authorUrl?: string | null;
  lastGeneratedAt?: string | null;
  mapId: string;
  initialHtml: string | null;          // wyswietlany HTML (moze byc swiezo wygenerowany LUB z DB)
  initialSavedHtml: string | null;     // PRAWDZIWA wartosc z DB author_page_html (null gdy nie zapisano)
  initialDebug: AuthorPageDebug | null;
  initialSchema?: SchemaPayload | null;
  onClose: () => void;
  onSaved?: (html: string) => void;
}

export function AuthorPagePreviewEditorModal({
  open,
  profileId,
  profileLabel,
  authorUrl,
  lastGeneratedAt,
  mapId,
  initialHtml,
  initialSavedHtml,
  initialDebug,
  initialSchema,
  onClose,
  onSaved,
}: AuthorPagePreviewEditorModalProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"preview" | "html" | "info">("preview");
  const [currentHtml, setCurrentHtml] = useState(initialHtml ?? "");
  const [savedHtml, setSavedHtml] = useState(initialSavedHtml ?? "");
  const [generatedSnapshot, setGeneratedSnapshot] = useState(initialHtml ?? "");
  const [debug, setDebug] = useState<AuthorPageDebug | null>(initialDebug);
  const [schema, setSchema] = useState<SchemaPayload | null>(initialSchema ?? null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const isDirty = currentHtml !== savedHtml;
  const canRestore = currentHtml !== generatedSnapshot && generatedSnapshot.length > 0;

  // Reset state na open (gdy modal pojawia sie z nowymi initials).
  // savedHtml = initialSavedHtml (DB truth) NIE initialHtml — inaczej swiezo
  // wygenerowany HTML bylby uznany za "zapisany" i Zapisz bylby disabled.
  useEffect(() => {
    if (open) {
      setCurrentHtml(initialHtml ?? "");
      setSavedHtml(initialSavedHtml ?? "");
      setGeneratedSnapshot(initialHtml ?? "");
      setDebug(initialDebug);
      setSchema(initialSchema ?? null);
      setActiveTab("preview");
      setIsEditMode(false);
    }
  }, [open, initialHtml, initialSavedHtml, initialDebug, initialSchema]);

  const handleCopy = async () => {
    if (!currentHtml) return;
    try {
      await navigator.clipboard.writeText(currentHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Blad kopiowania",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const handleRestore = () => {
    setCurrentHtml(generatedSnapshot);
    toast({ title: "Przywrocono", description: "HTML wrocil do ostatniej wygenerowanej wersji." });
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_writing_profiles")
        .update({
          author_page_html: currentHtml,
          author_page_generated_at: new Date().toISOString(),
        })
        .eq("id", profileId);

      if (error) throw new Error(error.message);

      setSavedHtml(currentHtml);
      onSaved?.(currentHtml);
      toast({
        title: "Strona autora zapisana",
        description: `${currentHtml.length} znakow HTML zapisanych w DB.`,
      });
    } catch (err) {
      toast({
        title: "Blad zapisu",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishWp = async () => {
    if (publishing || !currentHtml.trim()) return;
    setPublishing(true);
    try {
      const { data: map } = await (supabase as any)
        .from("thematic_maps")
        .select("website_id")
        .eq("id", mapId)
        .maybeSingle();
      if (!map?.website_id) {
        toast({ title: "Brak domeny", description: "Mapa nie ma przypisanej domeny (website).", variant: "destructive" });
        return;
      }
      const res = await publishHtmlAsWpPage({ websiteId: map.website_id, title: profileLabel, html: currentHtml });
      if (!res.ok) throw new Error(res.error || "Publikacja nie powiodla sie");
      await (supabase as any)
        .from("thematic_writing_profiles")
        .update({ author_page_wp_post_id: res.wpPageId ?? null })
        .eq("id", profileId);
      toast({ title: "Opublikowano strone autora (WP Page)", description: res.url || "Gotowe" });
    } catch (err) {
      toast({ title: "Blad publikacji", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  const handleRegenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await generateAuthorPage(profileId, mapId);
      if (!res.success || !res.html) {
        throw new Error(res.error || "Nieznany blad generacji");
      }
      setCurrentHtml(res.html);
      setGeneratedSnapshot(res.html);
      setDebug(res.debug || null);
      setSchema(res.schema || null);
      setActiveTab("preview");
      toast({
        title: "Wygenerowano ponownie",
        description: `${res.html.length} znakow • $${res.costUsd.toFixed(4)} • kliknij Zapisz aby utrwalic.`,
      });
    } catch (err) {
      toast({
        title: "Blad generacji",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditMode((v) => !v);
    if (!isEditMode) setActiveTab("html");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl border-pink-500/20">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                Strona autora — {profileLabel}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {authorUrl && (
                  <a
                    href={authorUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 hover:underline mr-2"
                  >
                    {authorUrl}
                  </a>
                )}
                {lastGeneratedAt && (
                  <span className="text-muted-foreground">
                    • Ostatnio: {new Date(lastGeneratedAt).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                )}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="default" className="bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/40">
                {currentHtml.length} chars
              </Badge>
              {isDirty && (
                <Badge variant="outline" className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40">
                  ● Niezapisane
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex-1">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="preview" className="gap-1.5">
              <Eye className="h-3.5 w-3.5" /> Podgląd
            </TabsTrigger>
            <TabsTrigger value="html" className="gap-1.5">
              <Code className="h-3.5 w-3.5" /> HTML
              {isEditMode && <Edit2 className="h-3 w-3 text-pink-600" />}
            </TabsTrigger>
            <TabsTrigger value="info" className="gap-1.5">
              <Info className="h-3.5 w-3.5" /> Info
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="mt-3">
            <div className="rounded-md border bg-card p-6 max-h-[60vh] overflow-auto">
              {currentHtml ? (
                <div
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: currentHtml }}
                />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Brak HTML. Kliknij "Wygeneruj ponownie" aby utworzyc strone autora.
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="html" className="mt-3 space-y-2">
            <textarea
              value={currentHtml}
              onChange={(e) => setCurrentHtml(e.target.value)}
              disabled={!isEditMode}
              spellCheck={false}
              className="w-full h-[55vh] p-3 rounded-md border bg-card font-mono text-xs leading-relaxed resize-none disabled:opacity-80 disabled:cursor-default focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/20"
              placeholder="HTML strony autora..."
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
              <span>
                {currentHtml.length} znakow
                {!isEditMode && (
                  <span className="ml-2 italic">(read-only — kliknij "Edytuj" aby zmieniac)</span>
                )}
              </span>
              {isDirty && (
                <span className="text-amber-700 dark:text-amber-400">
                  ● Niezapisane ({currentHtml.length - savedHtml.length > 0 ? "+" : ""}
                  {currentHtml.length - savedHtml.length} vs saved)
                </span>
              )}
            </div>
          </TabsContent>

          <TabsContent value="info" className="mt-3 space-y-3">
            <div className="rounded-md border bg-card p-4 max-h-[60vh] overflow-auto space-y-3">
              {!debug ? (
                <p className="text-sm text-muted-foreground italic">
                  Brak forensics. HTML zaladowany z DB (poprzednia generacja). Kliknij "Wygeneruj ponownie" aby uzyskac swiezy debug.
                </p>
              ) : (
                <>
                  <div>
                    <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Generacja</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs tabular-nums">
                      <div>
                        prompt_key: <span className="font-mono">{debug.prompt_key} v{debug.prompt_version ?? "?"}</span>
                      </div>
                      <div>
                        model: <span className="font-mono">{debug.model_id || debug.model}</span>
                      </div>
                      <div>
                        temperature: <span className="font-mono">{debug.temperature ?? "null"}</span>
                      </div>
                      <div>
                        duration: <span className="font-mono">{Math.round(debug.duration_ms / 1000)}s</span>
                      </div>
                      <div>
                        tokens in/out:{" "}
                        <span className="font-mono">
                          {debug.tokens_input ?? "?"} → {debug.tokens_output ?? "?"}
                        </span>
                      </div>
                      <div>
                        cost: <span className="font-mono">${debug.cost_usd.toFixed(4)}</span>
                      </div>
                      <div>
                        compiled chars: <span className="font-mono">{debug.compiled_prompt_chars}</span>
                      </div>
                      <div>
                        response chars: <span className="font-mono">{debug.raw_response_chars}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">
                      Kontekst (template_vars)
                    </p>
                    <pre className="p-2 bg-muted/40 rounded text-[10px] font-mono whitespace-pre-wrap max-h-60 overflow-auto">
                      {JSON.stringify(debug.template_vars, null, 2)}
                    </pre>
                  </div>

                  <details>
                    <summary className="text-xs uppercase tracking-wide font-semibold text-muted-foreground cursor-pointer hover:text-foreground">
                      Compiled prompt (FULL — {debug.compiled_prompt_chars} chars)
                    </summary>
                    <pre className="mt-2 p-2 bg-muted/40 rounded text-[10px] font-mono whitespace-pre-wrap max-h-80 overflow-auto">
                      {debug.compiled_prompt}
                    </pre>
                  </details>

                  <details>
                    <summary className="text-xs uppercase tracking-wide font-semibold text-muted-foreground cursor-pointer hover:text-foreground">
                      Raw LLM response (FULL — {debug.raw_response_chars} chars)
                    </summary>
                    <pre className="mt-2 p-2 bg-muted/40 rounded text-[10px] font-mono whitespace-pre-wrap max-h-80 overflow-auto">
                      {debug.raw_response_text}
                    </pre>
                  </details>
                </>
              )}

              {schema && (
                <details>
                  <summary className="text-xs uppercase tracking-wide font-semibold text-muted-foreground cursor-pointer hover:text-foreground">
                    Schema.org ProfilePage (JSON-LD)
                  </summary>
                  <pre className="mt-2 p-2 bg-muted/40 rounded text-[10px] font-mono whitespace-pre-wrap max-h-80 overflow-auto">
                    {JSON.stringify(schema, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex !justify-between gap-2 pt-3 border-t">
          <div className="flex items-center gap-1.5">
            <Button
              variant={isEditMode ? "default" : "outline"}
              size="sm"
              onClick={handleEditToggle}
              className={isEditMode ? "bg-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-500/50 hover:bg-pink-500/30" : ""}
              title={isEditMode ? "Wylacz tryb edycji (textarea read-only)" : "Wlacz tryb edycji HTML"}
            >
              <Edit2 className="mr-1.5 h-3.5 w-3.5" />
              {isEditMode ? "Edycja: ON" : "Edytuj"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRestore}
              disabled={!canRestore}
              title="Cofnij do ostatniej wygenerowanej wersji"
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Przywróć wygenerowane
            </Button>
          </div>

          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={!currentHtml}>
              {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
              {copied ? "Skopiowano" : "Kopiuj HTML"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegenerate}
              disabled={regenerating}
              title="Uruchom generateAuthorPage ponownie (nowy LLM call). NIE zapisuje automatycznie."
            >
              {regenerating ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Wygeneruj ponownie
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="bg-pink-500/15 hover:bg-pink-500/25 text-pink-700 dark:text-pink-300 border border-pink-500/50"
            >
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
              Zapisz{isDirty ? " ●" : ""}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePublishWp}
              disabled={publishing || !currentHtml.trim()}
              title="Publikuj stronę autora jako WP Page"
            >
              {publishing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Publikuj na WP
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
